import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { DatePicker, Icon } from '@livable/saas-react-components';
import { Button, FormControl, TextField, Typography } from '@mui/material';
import { formatISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlainInputTooltip from 'src/components/general/PlainInputTooltip';
import DropFileUploader from 'src/components/shared/DropFileUploader';
import { events, track } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import useLeaseDetailsStyles from './styles';
export const LeaseDetails = observer(_ref => {
  var _previouslyEnrolledTo, _previouslyEnrolledTo2, _previouslyEnrolledTo3, _previouslyEnrolledTo4;
  let previouslyEnrolledToUnit = _ref.previouslyEnrolledToUnit;
  const classes = useLeaseDetailsStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const fileId = store.enrollResident.enrollResidentInUnit.leaseAddendumFileId;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    filePreviewLink = _useState2[0],
    setFilePreviewLink = _useState2[1];
  const updateFilePreviewLink = async _ref2 => {
    let currentFile = _ref2.currentFile,
      fileIdExt = _ref2.fileIdExt;
    const fileNamePartials = currentFile.name.split('.');
    if (['doc', 'docx'].includes(fileNamePartials[fileNamePartials.length - 1])) {
      const requestFilePreviewLinkResp = await store.files.getRequestPreviewLinkAsync.run({
        fileId: fileIdExt
      });
      if (requestFilePreviewLinkResp !== null && requestFilePreviewLinkResp !== void 0 && requestFilePreviewLinkResp.isSuccessful) {
        setFilePreviewLink(requestFilePreviewLinkResp.response);
      }
    } else {
      const filePreviewLinkResp = await store.files.getPresignedFileDownloadUrlAsync.run({
        fileId: fileIdExt,
        isPreview: true
      });
      if (filePreviewLinkResp !== null && filePreviewLinkResp !== void 0 && filePreviewLinkResp.isSuccessful) {
        setFilePreviewLink(filePreviewLinkResp.response);
      }
    }
  };
  const handleFileChange = async currentFile => {
    if (!currentFile) {
      track(events.fileRemoved('Addendum'));
      await store.files.deleteFileAsync.run({
        fileId
      });
      store.enrollResident.updateLeaseAddendumAttachment('');
      store.enrollResident.updateLeaseAddendumAttachmentName('');
    } else {
      track(events.fileUploaded('Addendum'));
      let uploadedFileResult;
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        uploadedFileResult = await store.files.uploadFileViaPresignedUrlExperimentAsync.run({
          file: currentFile,
          createdBy: store.account.userProfile.id
        });
        await updateFilePreviewLink({
          currentFile,
          fileIdExt: uploadedFileResult.response
        });
      } else {
        uploadedFileResult = await store.files.uploadFileAsync.run({
          file: currentFile
        });
        await updateFilePreviewLink({
          currentFile,
          fileIdExt: uploadedFileResult.response
        });
      }
      if (!uploadedFileResult || !uploadedFileResult.isSuccessful) return;
      store.enrollResident.updateLeaseAddendumAttachment(uploadedFileResult.response);
      store.enrollResident.updateLeaseAddendumAttachmentName(currentFile.name);
    }
  };
  const handleLeaseStartDateOnly = date => {
    track(events.datePicked('Lease Start'));
    store.enrollResident.changeInUnitValue('leaseStartDateOnly', formatISO(date).split('T')[0]);
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('enrollment_modal.lease_details')), /*#__PURE__*/React.createElement("div", {
    className: classes.leaseInfo
  }, (previouslyEnrolledToUnit !== undefined || typeof previouslyEnrolledToUnit === 'object' && Object.keys(previouslyEnrolledToUnit).length) && (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 ? void 0 : previouslyEnrolledToUnit.leaseEndDateOnly) && (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 || (_previouslyEnrolledTo = previouslyEnrolledToUnit.accountData) === null || _previouslyEnrolledTo === void 0 ? void 0 : _previouslyEnrolledTo.firstName) && (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 || (_previouslyEnrolledTo2 = previouslyEnrolledToUnit.accountData) === null || _previouslyEnrolledTo2 === void 0 ? void 0 : _previouslyEnrolledTo2.lastName) && /*#__PURE__*/React.createElement("div", {
    className: classes.fieldLg
  }, /*#__PURE__*/React.createElement(Typography, {
    component: "p",
    marginTop: "20px",
    variant: "body2"
  }, t('enrollment_modal.living_note', {
    date: moment(previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 ? void 0 : previouslyEnrolledToUnit.leaseEndDateOnly).format('MM/DD/YYYY') || '12/11/2023',
    resident: (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 || (_previouslyEnrolledTo3 = previouslyEnrolledToUnit.accountData) === null || _previouslyEnrolledTo3 === void 0 ? void 0 : _previouslyEnrolledTo3.firstName) + ' ' + (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 || (_previouslyEnrolledTo4 = previouslyEnrolledToUnit.accountData) === null || _previouslyEnrolledTo4 === void 0 ? void 0 : _previouslyEnrolledTo4.lastName),
    interpolation: {
      escapeValue: false
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.fieldSm
  }, /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: true,
    error: !store.enrollResident.isValidField('leaseStartDateOnly')
  }, /*#__PURE__*/React.createElement(DatePicker, {
    value: store.enrollResident.enrollResidentInUnit.leaseStartDateOnly ? moment(store.enrollResident.enrollResidentInUnit.leaseStartDateOnly).toDate() : null,
    onChange: date => handleLeaseStartDateOnly(date),
    format: "MM/dd/yy",
    label: t('enrollment_modal.lease_start_date'),
    minDate: (previouslyEnrolledToUnit === null || previouslyEnrolledToUnit === void 0 ? void 0 : previouslyEnrolledToUnit.leaseEndDateOnly) && moment(previouslyEnrolledToUnit.leaseEndDateOnly).add(1, 'day').toDate()
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.groupWrapperContent
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(fileId ? 'attached' : '', " ").concat(classes.attachFile)
  }, /*#__PURE__*/React.createElement(DropFileUploader, {
    callback: handleFileChange,
    filePreviewLink: filePreviewLink,
    label: t('general.upload_addendum'),
    isUtilityForm: true,
    noPreview: true,
    tooltipText: t('enrollment_modal.upload_addendum_tooltip')
  }))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, t('enrollment_modal.who_else_is_on_lease')), store.enrollResident.enrollResidentInUnit.roommates.map((elem, i) => /*#__PURE__*/React.createElement("div", {
    className: classes.personForm,
    key: i
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fieldMd
  }, /*#__PURE__*/React.createElement(TextField, {
    error: !!store.enrollResident.enrollResidentInUnitRoommatesValidation[i].firstName,
    fullWidth: true,
    helperText: store.enrollResident.enrollResidentInUnitRoommatesValidation[i].firstName,
    label: t('general.first_name'),
    onBlur: () => track(events.filled('Roommate First Name')),
    onChange: e => store.enrollResident.changeRoommateUnitValue(i, 'firstName', e.target.value),
    value: store.enrollResident.enrollResidentInUnit.roommates[i].firstName,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.fieldMd
  }, /*#__PURE__*/React.createElement(TextField, {
    error: !!store.enrollResident.enrollResidentInUnitRoommatesValidation[i].lastName,
    fullWidth: true,
    helperText: store.enrollResident.enrollResidentInUnitRoommatesValidation[i].lastName,
    label: t('general.last_name'),
    onBlur: () => track(events.filled('Roommate Last Name')),
    onChange: e => store.enrollResident.changeRoommateUnitValue(i, 'lastName', e.target.value),
    value: store.enrollResident.enrollResidentInUnit.roommates[i].lastName,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.flexGrow
  }, /*#__PURE__*/React.createElement(TextField, {
    error: !!store.enrollResident.enrollResidentInUnitRoommatesValidation[i].email,
    fullWidth: true,
    helperText: store.enrollResident.enrollResidentInUnitRoommatesValidation[i].email,
    label: t('general.email_optional'),
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(PlainInputTooltip, {
        tooltipText: t('enrollment_modal.leaseholders_email_tooltip')
      })
    },
    onBlur: () => track(events.filled('Roommate Email')),
    onChange: e => store.enrollResident.changeRoommateUnitValue(i, 'email', e.target.value),
    value: store.enrollResident.enrollResidentInUnit.roommates[i].email,
    variant: "outlined"
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    size: "large",
    onClick: () => {
      track(events.clicked('Remove Roommate'));
      store.enrollResident.removeRoommateUnit(i);
    },
    'custom-attribute': '242-LeaseDetails'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "delete"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.mdSection
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      track(events.clicked('Add Roommate'));
      store.enrollResident.addRoommateUnit();
    },
    size: "small",
    variant: "contained",
    'custom-attribute': '243-LeaseDetails'
  }, t('enrollment_modal.add_person'))));
});